import { useState } from "react";
import { useNavigate } from "react-router-dom";
import StructuredStudy from "@/components/StructuredStudy";
import DeeperChat from "@/components/DeeperChat";
import ConcordancePanel from "@/components/ConcordancePanel";
import NotesPanel from "@/components/NotesPanel";
import SermonPanel from "@/components/SermonPanel";
import ShareStudyDialog from "@/components/ShareStudyDialog";
import { ChevronLeft, MessageCircle, X, Wrench } from "lucide-react";

// Desktop "Focused Reader" workspace (>= 768px).
// - Reading centre column with a quiet study-tools rail (collapsed concordance + notes).
// - "Can we go deeper?" is PINNED to the bottom of the reading column, always visible
//   while reading. Clicking it slides the conversation up in place (no new window),
//   and the companion greets first.
export default function DesktopStudyWorkspace({ session, setSession }) {
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMounted, setChatMounted] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  const BackLink = (
    <div className="flex items-center justify-between mb-8">
      <button
        data-testid="back-to-study"
        onClick={() => navigate("/study")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#00E5FF] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> New study
      </button>
      <ShareStudyDialog sessionId={session.id} title={session.study?.title} testid="share-study-desktop" />
    </div>
  );

  return (
    <div className="flex h-screen relative" data-testid="desktop-workspace">
      {/* Centre column: scrollable reading area + pinned Go Deeper footer */}
      <div className="flex-1 min-w-0 flex flex-col relative">
        <div className="flex-1 overflow-y-auto" data-testid="study-centre">
          <div className="max-w-[720px] mx-auto px-12 py-12">
            {BackLink}
            {/* Full study renders immediately — no opening-notice gate. */}
            <StructuredStudy
              session={session}
              hideStrongs
              onPassageChange={setSession ? (p) => setSession((s) => ({ ...s, passage: p })) : undefined}
              onStudyReload={setSession ? (data) => setSession(data) : undefined}
              showGenerators={!!setSession}
            />
          </div>
        </div>

        {/* Conversation panel — mounted once opened, hidden via CSS so history persists */}
        {chatMounted && (
          <div className={`absolute left-0 right-0 bottom-[80px] z-30 px-12 ${chatOpen ? "animate-in fade-in slide-in-from-bottom-4 duration-400" : "hidden"}`}>
            <div
              data-testid="deeper-panel-desktop"
              className="max-w-[720px] mx-auto h-[58vh] max-h-[560px] bg-[#0E131F] rounded-t-3xl shadow-[0_-8px_40px_rgba(0,0,0,0.14)] border border-[#1E273D] border-b-0 overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E273D] shrink-0">
                <div>
                  <div className="text-sm font-medium text-slate-200">Going deeper</div>
                  <div className="text-xs text-slate-500">A conversation, not a lecture</div>
                </div>
                <button
                  data-testid="deeper-close-desktop"
                  onClick={() => setChatOpen(false)}
                  className="p-2 text-slate-500 hover:text-slate-300"
                  title="Close"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <DeeperChat variant="embedded" sessionId={session.id} initialChat={session.chat || []} initialHighlights={session.highlights || []} />
            </div>
          </div>
        )}

        {/* Pinned, ALWAYS-visible Go Deeper button */}
        <div className="shrink-0 border-t border-[#1E273D] bg-[#0E131F]/85 backdrop-blur-sm px-12 py-4 z-40">
          <div className="max-w-[720px] mx-auto">
            <button
              data-testid="deeper-open-desktop"
              onClick={() => { setChatMounted(true); setChatOpen((o) => !o); }}
              className="w-full h-12 rounded-full bg-[#10222A] hover:bg-[#10222A] text-[#00B8CC] text-base font-medium border border-[#00E5FF]/40 transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
              {chatOpen ? "Hide conversation" : "Go Deeper"}
            </button>
          </div>
        </div>
      </div>

      {/* Quiet study-tools rail — ALWAYS present (even during the opening notice) so the
          reading column never reflows when the study loads. Collapsed to a slim placeholder;
          expands as an OVERLAY drawer that does not shift the page. */}
      <aside
        data-testid="study-tools-rail"
        className="w-14 shrink-0 h-screen flex flex-col items-center pt-6 border-l border-[#1E273D] bg-[#0E131F]/40"
      >
        <button
          data-testid="study-tools-toggle"
          onClick={() => setToolsOpen(true)}
          title="Study tools"
          aria-label="Open study tools"
          className="flex flex-col items-center gap-2 text-slate-500 hover:text-[#00E5FF] transition-colors"
        >
          <span className="w-9 h-9 rounded-xl bg-[#10222A] flex items-center justify-center">
            <Wrench className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <span className="text-[10px] uppercase tracking-widest [writing-mode:vertical-rl] rotate-180">Study tools</span>
        </button>
      </aside>

      {/* Overlay drawer — slides over the right side without reflowing the reading column */}
      {toolsOpen && (
        <>
          <div
            data-testid="study-tools-backdrop"
            onClick={() => setToolsOpen(false)}
            className="absolute inset-0 z-40 bg-black/10 animate-in fade-in duration-200"
          />
          <aside
            data-testid="study-right-panel"
            className="absolute right-0 top-0 h-screen w-[380px] max-w-[85vw] z-50 flex flex-col border-l border-[#1E273D] bg-[#0E131F] shadow-[0_0_40px_rgba(0,0,0,0.12)] animate-in slide-in-from-right duration-300"
          >
            <div className="shrink-0 px-6 py-4 border-b border-[#1E273D] flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">Study tools</span>
              <button
                data-testid="study-tools-close"
                onClick={() => setToolsOpen(false)}
                className="p-1.5 -mr-1.5 text-slate-500 hover:text-slate-300"
                title="Close study tools"
                aria-label="Close study tools"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-[#1E273D]">
              <ConcordancePanel session={session} defaultOpen={false} />
              <NotesPanel session={session} onNotesChange={setSession ? (t) => setSession((s) => ({ ...s, notes: t })) : undefined} />
              <SermonPanel session={session} />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
